import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useSetPage, useTypedTranslation} from '@app/hooks';
import {IMenuItem, mainMenuTypes} from '@app/components/Sidebar/menuObject';
import {
    genericErrorToast,
    getMenuItemById,
    hasOrgPermission,
    infiniteScrollItemsFetchLimit,
    translationVariable,
    useDebounce
} from '@app/helpers';
import {
    IDotMenu,
    IListPageBarEnabled,
    IPasswordManagerPolicies,
    IPasswordManagerPolicy,
    IRequirementSwitch,
    ITableHead,
    Order,
    OrderType,
    RequirementTypes
} from '@app/models';
import {PageLoader, PasslyButton, PasslyTable, RightSidebarModal} from '@app/components';
import {resetScroll, setExceptionHandler} from '@app/store/actions';
import {passwordManagerPolicyService} from '@app/services';
import {Add} from '@material-ui/icons';
import {ManageSystem, ManageVault} from '@app/store/constants';
import {confirm} from '@app/components/Modal/confirmModal';
import PasswordServerPolicyDetails
    from '@app/pages/passwordManager/components/passwordServerPolicyDetails/passwordServerPolicyDetails';
import {AxiosError} from 'axios';
import {FormMode} from '@app/helpers/enums';
import {handleSetSnackbar} from '@app/store/actions/snackbar.actions';

interface ITableData {
    checked: boolean;
    id: string;
    displayName: string;
    created: Date;
    lastModified: Date;
    onClick?: Function;
}

enum TableHeadEnum {
    displayName = 'displayName',
    created = 'created',
    lastModified = 'lastModified'
}

const minLength: number = 1;
const maxLength: number = 255;

const PasswordPolicyPage2 = () => {
    const menuItem: IMenuItem | undefined = getMenuItemById(mainMenuTypes.PasswordPolicy2);

    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [orderBy, setOrderBy] = useState('displayName');
    const [loadingData, setLoadingData] = useState(true);
    const [isDetailsLoading, setIsDetailsLoading] = useState(false);
    const [rows, setRows] = useState<ITableData[]>([]);
    const [orgAllowedToManageThisPage, setOrgAllowedToManageThisPage] = useState<boolean>(false);
    const [openDetailsPanelId, setOpenDetailsPanelId] = useState<string | null>();
    const [isOpenDetailsPanel, setIsOpenDetailsPanel] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState(0);
    const [searchField, setSearchField] = useState('');
    const [resetScrollCount, setResetScrollCount] = useState(0);
    const debouncedSearch = useDebounce(searchField, 500);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const [viewType, setViewType] = useState(FormMode.Add);
    const [requirementsSwitches, setRequirementsSwitches] = useState({
        uppercase: true,
        lowercase: true,
        digits: true,
        specials: true
    });

    const [displayName, setDisplayName] = useState<string>('');
    const [minimumLength, setMinimumLength] = useState<number>(minLength);
    const [maximumLength, setMaximumLength] = useState<number>(maxLength);

    const requirementsSwitchesData: IRequirementSwitch[] = [
        {
            type: RequirementTypes.digits,
            checked: requirementsSwitches.digits,
            label: 'PASSWORD_REQUIRE_DIGIT'
        },
        {
            type: RequirementTypes.specials,
            checked: requirementsSwitches.specials,
            label: 'PASSWORD_REQUIRE_SPECIAL',
            tooltip: 'PASSWORD_REQUIRE_SPECIAL_TOOLTIP'
        },
        {
            type: RequirementTypes.uppercase,
            checked: requirementsSwitches.uppercase,
            label: 'PASSWORD_REQUIRE_UPPERCASE_LETTER'
        },
        {
            type: RequirementTypes.lowercase,
            checked: requirementsSwitches.lowercase,
            label: 'PASSWORD_REQUIRE_LOWERCASE_LETTER'
        }
    ];

    const handleGetPolicyError = (error: AxiosError<any>) => {
        setIsDetailsLoading(false);
        genericErrorToast(error);
    };

    const handleGetPolicySuccess = (response: any) => {
        const results: IPasswordManagerPolicy = response.data;

        setIsDetailsLoading(false);

        if (results) {
            setDisplayName(results.displayName);
            setMinimumLength(results.minimumLength);
            setMaximumLength(results.maximumLength);
            setRequirementsSwitches({
                ...requirementsSwitches,
                [RequirementTypes.lowercase]: results.requireLowercase,
                [RequirementTypes.uppercase]: results.requireUppercase,
                [RequirementTypes.digits]: results.requireDigits,
                [RequirementTypes.specials]: results.requireSpecials
            });
        }
    };

    useEffect(() => {
        if (openDetailsPanelId) {
            setIsDetailsLoading(true);

            passwordManagerPolicyService.getPolicy2(openDetailsPanelId, handleGetPolicyError, handleGetPolicySuccess);
        }
    }, [openDetailsPanelId]);

    const onFormChange = (e: any) => {
        let { name, value } = e.target;

        switch (name) {
            case 'displayName':
                setDisplayName(value);
                break;
            case 'minimumLength':
                setMinimumLength(value);
                break;
            case 'maximumLength':
                setMaximumLength(value);
                break;
            default:
                break;
        }
    };

    const handleChange = (name: RequirementTypes, value: boolean) => () => {
        setRequirementsSwitches({ ...requirementsSwitches, [name]: !value });
    };

    const handleSubmit = () => {
        let jsonObject: any = {};

        jsonObject.displayName = displayName.trim();
        jsonObject.minimumLength = minimumLength;
        jsonObject.maximumLength = maximumLength;
        jsonObject.requireUppercase = requirementsSwitches.uppercase;
        jsonObject.requireLowercase = requirementsSwitches.lowercase;
        jsonObject.requireDigits = requirementsSwitches.digits;
        jsonObject.requireSpecials = requirementsSwitches.specials;

        if (viewType === FormMode.Add) {
            setIsDetailsLoading(true);
            passwordManagerPolicyService.addPolicy2(jsonObject, handleGetPolicyError, handleSubmitSuccess);
        } else if (openDetailsPanelId) {
            setIsDetailsLoading(true);
            passwordManagerPolicyService.editPolicy2(jsonObject, openDetailsPanelId, handleGetPolicyError, handleSubmitSuccess);
        }
    };

    const resetFormState = () => {
        setDisplayName('');

        setMinimumLength(minLength);
        setMaximumLength(maxLength);
        setRequirementsSwitches({
            [RequirementTypes.lowercase]: true,
            [RequirementTypes.uppercase]: true,
            [RequirementTypes.digits]: true,
            [RequirementTypes.specials]: true
        });
    };

    const handleSubmitSuccess = () => {
        let msg: string = '';

        if (viewType === FormMode.Add) {
            msg = translation('SUCCESS_ADD_PASSWORD_SERVER_POLICY').replace(translationVariable, displayName);
        } else {
            msg = translation('SUCCESS_EDIT_PASSWORD_SERVER_POLICY').replace(translationVariable, displayName);
        }

        setIsDetailsLoading(false);

        dispatch(handleSetSnackbar({ show: true, message: msg, severity: 'success' }));
        handlePageRefreshFromDialog(true);
        hideRightPanel();
    };

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: false
    };

    const showDetailsPanel = (e: any) => {
        setIsOpenDetailsPanel(true);
        setViewType(FormMode.Edit);
        setOpenDetailsPanelId(e.currentTarget.dataset.id);
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.displayName,
            label: 'PASSWORD_SERVER_POLICY_NAME',
            type: 'link',
            onClick: showDetailsPanel
        },
        {
            id: TableHeadEnum.created,
            label: 'CREATEDAT',
            type: 'date'
        },
        {
            id: TableHeadEnum.lastModified,
            label: 'PASSWORD_SERVER_POLICY_LAST_MODIFIED',
            type: 'date'
        }
    ];

    useSetPage(setListPageBarEnabledOptions, menuItem);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        setOrgAllowedToManageThisPage(hasOrgPermission([ManageVault, ManageSystem]));
    }, []);

    useEffect(() => {
        if (!loadingData) {
            setResetScrollCount(resetScrollCount + 1);
        }
    }, [debouncedSearch]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        passwordManagerPolicyService.getPolicies2(
            orderBy,
            order,
            infiniteScrollItemsFetchLimit,
            skipCount,
            handleError,
            handleSuccess(isReset),
            debouncedSearch
        );
    };

    const policyDetailsClick = (e: any) => {
        setIsOpenDetailsPanel(true);
        setViewType(FormMode.Edit);
        setOpenDetailsPanelId(e.currentTarget.dataset.id);
    };

    const hideRightPanel = () => {
        setIsOpenDetailsPanel(false);
        setOpenDetailsPanelId(null);
        resetFormState();
    };

    const handleSuccess = (isReset: boolean) => (response: any) => {
        const count = response.data.count;

        if (count !== undefined) {
            setTotalCount(count);
        }

        const results: IPasswordManagerPolicies[] = response.data.items;

        if (results) {
            let newRows = results.map((row: IPasswordManagerPolicies) => {
                const data: ITableData = {
                    checked: false,
                    id: row.id,
                    displayName: row.displayName,
                    created: row.created,
                    lastModified: row.lastModified
                };

                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (isReset) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }

        setLoadingData(false);
    };

    const handleError = (error: any) => {
        setLoadingData(false);

        if (error.response.status === 401 && error.response.status === 404) {
            setOrgAllowedToManageThisPage(false);
        } else {
            genericErrorToast(error);
        }
    };

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        setOrderBy(property);
        setLoadingData(true);
        dispatch(resetScroll());
    };

    const addPolicy = () => {
        setViewType(FormMode.Add);
        setIsOpenDetailsPanel(true);
    };

    const handlePageRefreshFromDialog = (refreshPage: boolean) => {
        if (refreshPage) {
            dispatch(resetScroll());
        }
    };

    const deleteSelected = () => {
        let ids: string[] = [];
        rows.forEach((row) => {
            if (row.checked) {
                ids.push(row.id);
            }
        });

        deletePolicyDialog(ids);
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deletePolicyDialog(ids);
    };

    const deletePolicyDialog = (ids: string[]) => {
        confirm(
            translation('DELETE_PASSWORD_SERVER_POLICY_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_PASSWORD_SERVER_POLICY_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                deletePolicies(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deletePolicies = (ids: string[]) => {
        let json: any = {};

        json.ids = [];

        ids.forEach(function (id) {
            json.ids.push(id);
        });

        passwordManagerPolicyService.deletePolicy2(json, handleDeleteError, handleDeleteSuccess);
    };

    const handleDeleteSuccess = () => {
        handlePageRefreshFromDialog(true);
        hideRightPanel();
    };

    const handleDeleteError = (error: AxiosError<any>) => {
        if (error.response?.data?.error) {
            const allMessages = error.response.data.error.message.replace('||', '<br /><br />');

            confirm(allMessages, translation('OK'), '', translation('SEVERITY_WARNING'), true).then(
                () => {
                    // do nothing on success
                },
                () => {
                    // do nothing on cancel
                }
            );
        } else {
            genericErrorToast(error);
        }
    };

    return (
        <>
            <RightSidebarModal
                title={viewType === FormMode.Add ? translation('ADD_POLICY') : translation('EDIT_AUTH_POLICY')}
                isOpen={isOpenDetailsPanel}
                onClose={hideRightPanel}
                onSubmit={handleSubmit}
                isSubmitButtonDisabled={displayName === '' || minimumLength > maximumLength}
                primaryBtnText={viewType === FormMode.Add ? translation('SAVE') : translation('APPLY')}
                secondaryBtnText={translation('CANCEL')}
            >
                {isOpenDetailsPanel ? (
                    <PasswordServerPolicyDetails
                        onChange={onFormChange}
                        handleRequirementsChange={handleChange}
                        displayName={displayName}
                        minimumLength={minimumLength}
                        maximumLength={maximumLength}
                        requirementsSwitchesData={requirementsSwitchesData}
                        loadingData={isDetailsLoading}
                    />
                ) : (
                    <PageLoader useOverlay={true} isAbsolutePosition={true} />
                )}
            </RightSidebarModal>

            {orgAllowedToManageThisPage ? (
                <>
                    <div className='passwordVaults'>
                        <div className='vaults__section-head-inputs vaults__section-head-inputs-right'>
                            {orgAllowedToManageThisPage && (
                                <PasslyButton color='primary' variant='contained' onClick={addPolicy}>
                                    <Add color='inherit' />
                                    {translation('ADD_PASSWORD_SERVER_POLICY')}
                                </PasslyButton>
                            )}
                        </div>
                    </div>
                    <PasslyTable
                        rows={rows}
                        setRows={setRows}
                        order={order}
                        orderBy={orderBy}
                        handleOrderChange={createSortHandler}
                        headerItems={headCells}
                        rowsCount={totalCount}
                        onFetch={fetchMoreData}
                        withCheckboxes={orgAllowedToManageThisPage}
                        emptyListHelper={translation('EMPTY_PASSWORD_SERVER_POLICY_LIST')}
                        loadingData={loadingData}
                        searchValue={searchField}
                        setSearchValue={setSearchField}
                        editRow={policyDetailsClick}
                        deleteRow={deleteRow}
                        resetLocal={resetScrollCount}
                        actionMenuItems={hasOrgPermission([ManageVault, ManageSystem]) ? actionMenuItems : undefined}
                    />{' '}
                </>
            ) : (
                <div className='table--empty'>{translation('NO_ORG_PASSWORD_SERVER_POLICY_PERMISSIONS')}</div>
            )}
        </>
    );
};

export default PasswordPolicyPage2;
